package com.smashvoter.screens

import androidx.compose.runtime.*
import com.smashvoter.SmashColors
import com.smashvoter.smashNavButton
import com.smashvoter.api.ApiClient
import com.smashvoter.api.RankedPerson
import com.smashvoter.api.VoteLimitExceededException
import com.smashvoter.api.VotePairItem
import com.smashvoter.state.AppState
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLStyleElement
import kotlin.math.roundToInt

private const val PAIR_SCREEN_STYLE_ID = "pair-screen-responsive-styles"

private val pairScreenCss = """
    .pair-card {
        width: 240px;
        box-sizing: border-box;
    }
    .pair-card-image {
        width: 180px;
        height: 180px;
    }
    @media (max-width: 600px) {
        .pair-cards-container {
            flex-direction: column;
            align-items: center;
        }
        .pair-card {
            width: 100%;
            max-width: 360px;
        }
        .pair-card-image {
            width: 220px;
            height: 220px;
        }
    }
""".trimIndent()

@Composable
fun PairScreen(apiClient: ApiClient) {
    // Inject responsive CSS into <head> via JS — avoids Compose HTML StyleSheet API limitations
    DisposableEffect(Unit) {
        if (document.getElementById(PAIR_SCREEN_STYLE_ID) == null) {
            val styleEl = document.createElement("style") as HTMLStyleElement
            styleEl.id = PAIR_SCREEN_STYLE_ID
            styleEl.textContent = pairScreenCss
            document.head?.appendChild(styleEl)
        }
        onDispose {
            document.getElementById(PAIR_SCREEN_STYLE_ID)?.remove()
        }
    }

    val scope = rememberCoroutineScope()
    val sessionId = AppState.sessionId ?: return

    var allPairs by remember { mutableStateOf<List<VotePairItem>>(emptyList()) }
    var votesMax by remember { mutableStateOf(26) }
    var isLoading by remember { mutableStateOf(true) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var voteLimitReached by remember { mutableStateOf(false) }

    val currentPair: VotePairItem? = allPairs.firstOrNull { !it.used }
    val votesUsed = allPairs.count { it.used }

    fun markPairUsed(pairId: String) {
        allPairs = allPairs.map { if (it.id == pairId) it.copy(used = true) else it }
    }

    fun loadPairs() {
        isLoading = true
        errorMessage = null
        scope.launch {
            try {
                val response = apiClient.getPairs(sessionId)
                allPairs = response.pairs
                votesMax = response.votesMax
                if (response.pairs.all { it.used }) voteLimitReached = true
            } catch (_: VoteLimitExceededException) {
                voteLimitReached = true
            } catch (_: Throwable) {
                errorMessage = "Failed to load vote pairs. Please try again."
            } finally {
                isLoading = false
            }
        }
    }

    LaunchedEffect(Unit) { loadPairs() }

    Div(attrs = {
        style {
            minHeight(100.vh)
            backgroundColor(Color(SmashColors.background))
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
        }
    }) {
        // Navigation bar
        Div(attrs = {
            style {
                backgroundColor(Color(SmashColors.surface))
                property("border-bottom", "1px solid ${SmashColors.border}")
                padding(14.px, 24.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            // Logo + wordmark
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(10.px)
                }
            }) {
                Img(src = "smash-logo.svg", attrs = {
                    style {
                        width(36.px)
                        height(36.px)
                        property("flex-shrink", "0")
                    }
                })
                Span(attrs = {
                    style {
                        color(Color(SmashColors.textPrimary))
                        fontSize(20.px)
                        fontWeight("900")
                        letterSpacing((-0.3).px)
                    }
                }) {
                    Text("SmashVoter")
                }
            }
            Div {
                Button(attrs = {
                    onClick {
                        scope.launch {
                            try { apiClient.logout(sessionId) } catch (_: Exception) {}
                            AppState.clearSession()
                        }
                    }
                    style { smashNavButton(); marginLeft(8.px) }
                }) { Text("Logout") }
            }
        }

        // Main content
        Div(attrs = {
            style {
                flex(1)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                padding(24.px)
            }
        }) {
            when {
                voteLimitReached -> VotingCompleteScreen()
                isLoading -> P(attrs = { style { color(Color(SmashColors.textSecondary)) } }) {
                    Text("Loading…")
                }
                errorMessage != null -> {
                    P(attrs = { style { color(Color(SmashColors.error)) } }) { Text(errorMessage!!) }
                    Button(attrs = {
                        onClick { loadPairs() }
                        style {
                            padding(10.px, 20.px)
                            backgroundColor(Color(SmashColors.accent))
                            color(Color(SmashColors.accentDark))
                            border(0.px, LineStyle.None, Color.transparent)
                            borderRadius(8.px)
                            fontWeight("700")
                            property("cursor", "pointer")
                        }
                    }) { Text("Retry") }
                }
                currentPair != null -> {
                    H2(attrs = {
                        style {
                            marginBottom(4.px)
                            color(Color(SmashColors.textPrimary))
                            fontSize(24.px)
                            fontWeight("700")
                        }
                    }) { Text("Who is better?") }

                    P(attrs = {
                        style {
                            color(Color(SmashColors.textSecondary))
                            marginBottom(20.px)
                            marginTop(4.px)
                            fontSize(14.px)
                        }
                    }) { Text("Click the person you'd vote for") }

                    VoteProgressBar(votesUsed = votesUsed, votesMax = votesMax)

                    Div(attrs = {
                        classes("pair-cards-container")
                        style {
                            display(DisplayStyle.Flex)
                            gap(24.px)
                            justifyContent(JustifyContent.Center)
                            alignItems(AlignItems.Center)
                            marginTop(28.px)
                            width(100.percent)
                        }
                    }) {
                        PersonCard(currentPair.person1) { winner ->
                            scope.launch {
                                isLoading = true
                                try {
                                    apiClient.vote(currentPair.id, winner.id, currentPair.person2.id, sessionId)
                                    markPairUsed(currentPair.id)
                                    if (allPairs.all { it.used }) voteLimitReached = true
                                } catch (_: VoteLimitExceededException) {
                                    voteLimitReached = true
                                } catch (_: Throwable) {
                                    errorMessage = "Failed to submit vote."
                                } finally {
                                    isLoading = false
                                }
                            }
                        }

                        // VS divider
                        Div(attrs = {
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                fontSize(20.px)
                                fontWeight("900")
                                color(Color(SmashColors.accent))
                                letterSpacing(1.px)
                            }
                        }) { Text("VS") }

                        PersonCard(currentPair.person2) { winner ->
                            scope.launch {
                                isLoading = true
                                try {
                                    apiClient.vote(currentPair.id, winner.id, currentPair.person1.id, sessionId)
                                    markPairUsed(currentPair.id)
                                    if (allPairs.all { it.used }) voteLimitReached = true
                                } catch (_: VoteLimitExceededException) {
                                    voteLimitReached = true
                                } catch (_: Throwable) {
                                    errorMessage = "Failed to submit vote."
                                } finally {
                                    isLoading = false
                                }
                            }
                        }
                    }

                    // Skip button
                    Button(attrs = {
                        onClick {
                            scope.launch {
                                isLoading = true
                                try {
                                    apiClient.skip(currentPair.id, sessionId)
                                    markPairUsed(currentPair.id)
                                    if (allPairs.all { it.used }) voteLimitReached = true
                                } catch (_: VoteLimitExceededException) {
                                    voteLimitReached = true
                                } catch (_: Throwable) {
                                    markPairUsed(currentPair.id)
                                } finally {
                                    isLoading = false
                                }
                            }
                        }
                        style {
                            marginTop(20.px)
                            padding(8.px, 20.px)
                            backgroundColor(Color.transparent)
                            color(Color(SmashColors.textSecondary))
                            border(1.px, LineStyle.Solid, Color(SmashColors.border))
                            borderRadius(8.px)
                            fontSize(13.px)
                            property("cursor", "pointer")
                            property("transition", "color 0.15s, border-color 0.15s")
                        }
                    }) { Text("Skip pair") }
                }
                else -> VotingCompleteScreen()
            }
        }
    }
}

@Composable
private fun VoteProgressBar(votesUsed: Int, votesMax: Int) {
    val remaining = votesMax - votesUsed
    val progressPct = (votesUsed.toDouble() / votesMax * 100).coerceIn(0.0, 100.0)

    Div(attrs = {
        style {
            width(100.percent)
            maxWidth(320.px)
            marginBottom(4.px)
        }
    }) {
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(6.px)
            }
        }) {
            Span(attrs = {
                style { fontSize(13.px); color(Color(SmashColors.textSecondary)) }
            }) { Text("$votesUsed / $votesMax votes cast") }
            Span(attrs = {
                style {
                    fontSize(13.px)
                    color(Color(SmashColors.accent))
                    fontWeight("600")
                }
            }) { Text("$remaining left") }
        }
        // Track
        Div(attrs = {
            style {
                width(100.percent)
                height(6.px)
                backgroundColor(Color(SmashColors.surfaceRaised))
                borderRadius(3.px)
                property("overflow", "hidden")
            }
        }) {
            // Fill
            Div(attrs = {
                style {
                    width(progressPct.roundToInt().percent)
                    height(100.percent)
                    backgroundColor(Color(SmashColors.accent))
                    borderRadius(3.px)
                    property("transition", "width 0.3s ease")
                }
            })
        }
    }
}

@Composable
private fun VotingCompleteScreen() {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            textAlign("center")
            padding(32.px)
            maxWidth(480.px)
            backgroundColor(Color(SmashColors.surface))
            borderRadius(16.px)
            border(1.px, LineStyle.Solid, Color(SmashColors.border))
            property("box-shadow", "0 8px 32px rgba(0,0,0,0.5)")
        }
    }) {
        Div(attrs = { style { fontSize(64.px); marginBottom(16.px) } }) {
            Text("🎉")
        }
        H2(attrs = {
            style {
                color(Color(SmashColors.accent))
                marginBottom(12.px)
                marginTop(0.px)
                fontSize(28.px)
                fontWeight("700")
            }
        }) { Text("You're done!") }
        P(attrs = {
            style {
                color(Color(SmashColors.textSecondary))
                fontSize(15.px)
                lineHeight("1.6")
                marginBottom(0.px)
            }
        }) {
            Text("You've cast all your votes. Thank you for participating — your votes help determine the final ranking!")
        }
    }
}

@Composable
private fun PersonCard(person: RankedPerson, onVote: (RankedPerson) -> Unit) {
    // Hover state tracked via JS class toggling via onMouseOver/onMouseOut
    var hovered by remember { mutableStateOf(false) }

    Div(attrs = {
        classes("pair-card")
        onClick { onVote(person) }
        onMouseOver { hovered = true }
        onMouseOut  { hovered = false }
        style {
            backgroundColor(Color(SmashColors.surface))
            borderRadius(16.px)
            border(
                1.px,
                LineStyle.Solid,
                Color(if (hovered) SmashColors.accent else SmashColors.border)
            )
            property(
                "box-shadow",
                if (hovered)
                    "0 0 0 1px ${SmashColors.accent}, 0 8px 32px rgba(225,13,5,0.25)"
                else
                    "0 4px 16px rgba(0,0,0,0.5)"
            )
            property(
                "transform",
                if (hovered) "translateY(-4px) scale(1.02)" else "translateY(0) scale(1)"
            )
            property("transition", "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease")
            property("cursor", "pointer")
            property("overflow", "hidden")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
        }
    }) {
        // Photo area — dark tray
        Div(attrs = {
            style {
                backgroundColor(Color(SmashColors.surfaceRaised))
                padding(20.px, 20.px, 12.px, 20.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
            }
        }) {
            Img(src = person.photoUrl, attrs = {
                classes("pair-card-image")
                style {
                    property("object-fit", "contain")
                    property("display", "block")
                    property("transition", "transform 0.18s ease")
                    if (hovered) property("transform", "scale(1.06)") else property("transform", "scale(1)")
                }
            })
        }

        // Name + vote CTA
        Div(attrs = {
            style {
                padding(14.px, 16.px, 16.px, 16.px)
                textAlign("center")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(10.px)
            }
        }) {
            H3(attrs = {
                style {
                    margin(0.px)
                    color(Color(SmashColors.textPrimary))
                    fontSize(16.px)
                    fontWeight("700")
                    letterSpacing((-0.2).px)
                }
            }) { Text(person.name) }

            // Vote button — full-width inside the card
            Div(attrs = {
                style {
                    padding(9.px, 0.px)
                    backgroundColor(
                        Color(if (hovered) SmashColors.accentButton else SmashColors.surfaceRaised)
                    )
                    color(
                        Color(if (hovered) SmashColors.accentDark else SmashColors.textSecondary)
                    )
                    borderRadius(8.px)
                    fontSize(13.px)
                    fontWeight("700")
                    letterSpacing(0.8.px)
                    property("text-transform", "uppercase")
                    property("transition", "background-color 0.18s, color 0.18s")
                }
            }) {
                Text("Vote")
            }
        }
    }
}


